import React, { useEffect, useState } from "react";
import "../assets/scss/layout/admin.scss";
// AdminPanel.jsx
// Single-file React admin panel. Drop into your React app (e.g. src/AdminPanel.jsx)
// Usage: import AdminPanel from './AdminPanel'; then render <AdminPanel />
// Requirements: your backend running at https://swiftexprofit.com and an admin JWT saved to localStorage as 'token'

const API_BASE = process.env.REACT_APP_API_BASE || "https://swiftexprofit.com";
const PRIMARY = "#16a34a";

function fetchWithAuth(url, options = {}) {
  const token = localStorage.getItem("token");
  const headers = options.headers || {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return fetch(url, { ...options, headers: { "Content-Type": "application/json", ...headers } });
}

function useDebounce(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function LoginModal({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Login failed");
      localStorage.setItem("token", data.token);
      onLogin();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="modalOverlay">
      <div className="modaltest">
        <h2>Admin Login</h2>
        <form onSubmit={submit} className="form">
          <label>
            Email
            <input value={email} onChange={(e) => setEmail(e.target.value)} required />
          </label>
          <label>
            Password
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </label>
          {error && <div className="error">{error}</div>}
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button type="submit" className="btn primary" disabled={loading}>
              {loading ? "Logging..." : "Login"}
            </button>
            <button type="button" className="btn" onClick={() => { setEmail('admin@example.com'); setPassword('password'); }}>
              Fill demo
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="modalOverlay" onMouseDown={onClose}>
      <div className="modaltest" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modalHeader">
          <h3>{title}</h3>
          <button className="btn ghost" onClick={onClose}>&times;</button>
        </div>
        <div className="modalBody">{children}</div>
      </div>
    </div>
  );
}

function UserForm({ initial = {}, onSubmit, submitLabel = "Save" }) {
  const [username, setUsername] = useState(initial.username || "");
  const [email, setEmail] = useState(initial.email || "");
  const [phone, setPhone] = useState(initial.phone || "");
  const [password, setPassword] = useState("");
  const [isAdmin, setIsAdmin] = useState(initial.is_admin === 1);
  const [balance, setBalance] = useState(initial.balance != null ? initial.balance : "0");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e && e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const body = { username, email, phone, is_admin: isAdmin ? 1 : 0 };
      if (password) body.password = password;
      if (balance !== "") body.balance = parseFloat(balance);
      await onSubmit(body);
    } catch (err) {
      setError(err.message || "Failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={submit} className="form">
      <label>
        Username
        <input value={username} onChange={(e) => setUsername(e.target.value)} required />
      </label>
      <label>
        Email
        <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required />
      </label>
      <label>
        Phone
        <input value={phone} onChange={(e) => setPhone(e.target.value)} required />
      </label>
      <label>
        Password
        <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" placeholder={initial.id ? "Leave blank to keep" : ""} />
      </label>
      <label>
        Balance
        <input value={balance} onChange={(e) => setBalance(e.target.value)} type="number" step="0.01" />
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input type="checkbox" checked={isAdmin} onChange={(e) => setIsAdmin(e.target.checked)} /> Is Admin
      </label>
      {error && <div className="error">{error}</div>}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button type="submit" className="btn primary" disabled={loading}>{loading ? 'Saving...' : submitLabel}</button>
      </div>
    </form>
  );
}

function AmountForm({ type = 'deposit', onSubmit }) {
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  async function submit(e) {
    e && e.preventDefault();
    setError("");
    const val = parseFloat(amount);
    if (!val || val <= 0) return setError("Enter valid amount");
    setLoading(true);
    try {
      await onSubmit(val);
    } catch (err) {
      setError(err.message || 'Failed');
    } finally {
      setLoading(false);
    }
  }
  return (
    <form onSubmit={submit} className="form">
      <label>
        Amount ({type})
        <input value={amount} onChange={(e) => setAmount(e.target.value)} type="number" step="0.01" required />
      </label>
      {error && <div className="error">{error}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button className="btn primary" type="submit" disabled={loading}>{loading ? 'Processing...' : 'Submit'}</button>
      </div>
    </form>
  );
}

function TransactionHistory({ userId, username }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [btcPrice, setBtcPrice] = useState(null);
  const [priceLoading, setPriceLoading] = useState(true);

  useEffect(() => {
    loadTransactions();
    loadBTCPrice();
  }, [userId]);

  async function loadBTCPrice() {
    setPriceLoading(true);
    try {
      const res = await fetch(`${API_BASE}/price/btc`);
      if (!res.ok) throw new Error('Failed to fetch BTC price');
      const data = await res.json();
      setBtcPrice(data.btc_usd);
    } catch (err) {
      console.error('Failed to load BTC price:', err);
      setBtcPrice(null);
    } finally {
      setPriceLoading(false);
    }
  }

  async function loadTransactions() {
    setLoading(true);
    setError("");
    try {
      const res = await fetchWithAuth(`${API_BASE}/admin/transactions/${userId}`);
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || 'Failed to load transactions');
      }
      const data = await res.json();
      setTransactions(data.transactions || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function formatDate(dateString) {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  function calculateUSD(btcAmount) {
    if (!btcPrice) return 'N/A';
    return (parseFloat(btcAmount) * btcPrice).toFixed(2);
  }

  if (loading) {
    return <div className="loading">Loading transactions...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="transactionHistory">
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 14, color: '#666' }}>
          {transactions.length} transaction{transactions.length !== 1 ? 's' : ''}
        </div>
        {priceLoading ? (
          <div style={{ fontSize: 12, color: '#999' }}>Loading BTC price...</div>
        ) : btcPrice ? (
          <div style={{ fontSize: 12, color: '#666' }}>
            BTC Price: ${btcPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        ) : (
          <div style={{ fontSize: 12, color: '#999' }}>BTC price unavailable</div>
        )}
      </div>

      {transactions.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          No transactions yet
        </div>
      ) : (
        <div className="tableWrap" style={{ maxHeight: 500, overflowY: 'auto' }}>
          <table className="usersTable" style={{ fontSize: 14 }}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Amount (BTC)</th>
                <th>Amount (USD)</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map(tx => (
                <tr key={tx.id}>
                  <td>{formatDate(tx.date)}</td>
                  <td>
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      borderRadius: 4,
                      fontSize: 12,
                      fontWeight: 600,
                      backgroundColor: tx.type === 'deposit' ? '#dcfce7' : '#fee2e2',
                      color: tx.type === 'deposit' ? '#166534' : '#991b1b'
                    }}>
                      {tx.type.charAt(0).toUpperCase() + tx.type.slice(1)}
                    </span>
                  </td>
                  <td style={{ fontFamily: 'monospace', fontWeight: 500 }}>
                    {parseFloat(tx.amount).toFixed(8)} BTC
                  </td>
                  <td style={{ fontFamily: 'monospace', fontWeight: 500 }}>
                    ${calculateUSD(tx.amount)}
                  </td>
                  <td>
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      borderRadius: 4,
                      fontSize: 12,
                      backgroundColor: tx.status === 'completed' ? '#dbeafe' : '#fef3c7',
                      color: tx.status === 'completed' ? '#1e40af' : '#92400e'
                    }}>
                      {tx.status || 'completed'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function AdminPanel() {
  const [users, setUsers] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [showLogin, setShowLogin] = useState(!localStorage.getItem('token'));
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalType, setModalType] = useState(null); // 'add', 'edit', 'deposit', 'withdraw', 'transactions'
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (showLogin) return;
    loadUsers(page, limit, debouncedSearch);
  }, [page, limit, debouncedSearch, refreshKey, showLogin]);

  async function loadUsers(p = 1, lim = 10, q = '') {
    setLoading(true);
    try {
      // If user is searching, fetch large limit and filter client-side
      if (q && q.length > 0) {
        const res = await fetchWithAuth(`${API_BASE}/users?page=1&limit=100000`);
        if (!res.ok) throw new Error('Failed to fetch users');
        const data = await res.json();
        const filtered = data.users.filter(u => (
          u.username?.toLowerCase().includes(q.toLowerCase()) ||
          u.email?.toLowerCase().includes(q.toLowerCase()) ||
          (u.phone || '').toLowerCase().includes(q.toLowerCase())
        ));
        setUsers(filtered.slice((p-1)*lim, p*lim));
        setTotalPages(Math.ceil(filtered.length / lim) || 1);
        setPage(p);
      } else {
        const res = await fetchWithAuth(`${API_BASE}/users?page=${p}&limit=${lim}`);
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.message || 'Failed to fetch');
        }
        const data = await res.json();
        setUsers(data.users || []);
        setTotalPages(data.totalPages || 1);
        setPage(data.currentPage || p);
      }
    } catch (err) {
      console.error(err);
      alert(err.message?.toString() || 'Failed');
    } finally {
      setLoading(false);
    }
  }

  function requireAuthRetry() {
    // if 401/403, force login
    localStorage.removeItem('token');
    setShowLogin(true);
  }

  async function handleAdd(body) {
    const res = await fetchWithAuth(`${API_BASE}/admin/add`, { method: 'POST', body: JSON.stringify(body) });
    if (!res.ok) {
      if (res.status === 401 || res.status === 403) { requireAuthRetry(); throw new Error('Auth required'); }
      const d = await res.json(); throw new Error(d.message || 'Add failed');
    }
    setModalType(null);
    setRefreshKey(k => k + 1);
  }

  async function handleEdit(body) {
    if (!selectedUser) return;
    const res = await fetchWithAuth(`${API_BASE}/admin/update/${selectedUser.id}`, { method: 'PUT', body: JSON.stringify(body) });
    if (!res.ok) {
      if (res.status === 401 || res.status === 403) { requireAuthRetry(); throw new Error('Auth required'); }
      const d = await res.json(); throw new Error(d.message || 'Update failed');
    }
    setModalType(null);
    setSelectedUser(null);
    setRefreshKey(k => k + 1);
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete user?')) return;
    const res = await fetchWithAuth(`${API_BASE}/admin/delete/${id}`, { method: 'DELETE' });
    if (!res.ok) {
      if (res.status === 401 || res.status === 403) { requireAuthRetry(); alert('Auth required'); return; }
      const d = await res.json(); alert(d.message || 'Delete failed'); return;
    }
    setRefreshKey(k => k + 1);
  }

  async function handleDeposit(id, amount) {
    const res = await fetchWithAuth(`${API_BASE}/admin/deposit/${id}`, { method: 'POST', body: JSON.stringify({ amount }) });
    if (!res.ok) {
      if (res.status === 401 || res.status === 403) { requireAuthRetry(); throw new Error('Auth required'); }
      const d = await res.json(); throw new Error(d.message || 'Deposit failed');
    }
    setModalType(null);
    setSelectedUser(null);
    setRefreshKey(k => k + 1);
  }

  async function handleWithdraw(id, amount) {
    const res = await fetchWithAuth(`${API_BASE}/admin/withdraw/${id}`, { method: 'POST', body: JSON.stringify({ amount }) });
    if (!res.ok) {
      if (res.status === 401 || res.status === 403) { requireAuthRetry(); throw new Error('Auth required'); }
      const d = await res.json(); throw new Error(d.message || 'Withdraw failed');
    }
    setModalType(null);
    setSelectedUser(null);
    setRefreshKey(k => k + 1);
  }

  function logout() {
    localStorage.removeItem('token');
    setShowLogin(true);
  }

  return (
    <div className="adminRoot">
      <header className="topbar">
        <h1>Admin Panel</h1>
        <div className="actions">
          <input placeholder="Search users..." value={search} onChange={(e) => setSearch(e.target.value)} />
          <button className="btn" onClick={() => { setModalType('add'); setSelectedUser(null); }}>Add User</button>
          <button className="btn ghost" onClick={() => { localStorage.removeItem('token'); setShowLogin(true); }}>Logout</button>
        </div>
      </header>

      <main className="container">
        <section className="controlsRow">
          <div>Page</div>
          <div className="pager">
            <button className="btn" disabled={page <= 1 || loading} onClick={() => setPage(1)}>«</button>
            <button className="btn" disabled={page <= 1 || loading} onClick={() => setPage(p => Math.max(1, p-1))}>‹</button>
            <span> {page} / {totalPages} </span>
            <button className="btn" disabled={page >= totalPages || loading} onClick={() => setPage(p => Math.min(totalPages, p+1))}>›</button>
            <button className="btn" disabled={page >= totalPages || loading} onClick={() => setPage(totalPages)}>»</button>
            <select value={limit} onChange={(e) => setLimit(parseInt(e.target.value))}>
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
        </section>

        <section className="tableWrap">
          {loading ? <div className="loading">Loading...</div> : (
            <table className="usersTable">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Balance</th>
                  <th>Admin</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center' }}>No users</td></tr>}
                {users.map(u => (
                  <tr key={u.id}>
                    <td>{u.id}</td>
                    <td>{u.username}</td>
                    <td>{u.email}</td>
                    <td>{u.phone}</td>
                    <td>{parseFloat(u.balance).toFixed(2)} BTC</td>
                    <td>{u.is_admin === 1 ? 'Yes' : 'No'}</td>
                    <td className="actionsCol">
                      <button className="btn" onClick={() => { setSelectedUser(u); setModalType('edit'); }}>Edit</button>
                      <button className="btn" onClick={() => handleDelete(u.id)}>Delete</button>
                      <button className="btn" onClick={() => { setSelectedUser(u); setModalType('deposit'); }}>Deposit</button>
                      <button className="btn" onClick={() => { setSelectedUser(u); setModalType('withdraw'); }}>Withdraw</button>
                      <button className="btn" onClick={() => { setSelectedUser(u); setModalType('transactions'); }}>History</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

      </main>

      {showLogin && <LoginModal onLogin={() => { setShowLogin(false); setRefreshKey(k => k + 1); }} />}

      {modalType === 'add' && (
        <Modal title="Add user" onClose={() => setModalType(null)}>
          <UserForm onSubmit={handleAdd} submitLabel="Add" />
        </Modal>
      )}

      {modalType === 'edit' && selectedUser && (
        <Modal title={`Edit user #${selectedUser.id}`} onClose={() => { setModalType(null); setSelectedUser(null); }}>
          <UserForm initial={selectedUser} onSubmit={handleEdit} submitLabel="Update" />
        </Modal>
      )}

      {modalType === 'deposit' && selectedUser && (
        <Modal title={`Deposit to ${selectedUser.username}`} onClose={() => { setModalType(null); setSelectedUser(null); }}>
          <AmountForm type="deposit" onSubmit={(amount) => handleDeposit(selectedUser.id, amount)} />
        </Modal>
      )}

      {modalType === 'withdraw' && selectedUser && (
        <Modal title={`Withdraw from ${selectedUser.username}`} onClose={() => { setModalType(null); setSelectedUser(null); }}>
          <AmountForm type="withdraw" onSubmit={(amount) => handleWithdraw(selectedUser.id, amount)} />
        </Modal>
      )}

      {modalType === 'transactions' && selectedUser && (
        <Modal title={`Transaction History - ${selectedUser.username}`} onClose={() => { setModalType(null); setSelectedUser(null); }}>
          <TransactionHistory userId={selectedUser.id} username={selectedUser.username} />
        </Modal>
      )}
    </div>
  );
}